// @ts-nocheck
import Box from '@mui/material/Box';
import Editor from '../../../Editor/Editor';
import ServiceCards from '../../../Trip/DetailedItinerary/ServicesView/ServiceCards';
import { useTranslation } from 'react-i18next';
import { Sun, File } from '@phosphor-icons/react';
import { XGModal } from '@xg-cl/xg-cl';
import { pathReference, imageSize } from '../../../../constants/files';
import {
  getBucketFileUrl,
  getBucketImageUrl
} from '../../../../utils/resourcePathHelper';
import * as P from '@phosphor-icons/react';
import * as PropTypes from 'prop-types';
import './LibraryPreview.css';

// Map service types to their respective card components
const ServiceCardComponent = {
  ACCO: ServiceCards.HotelCard,
  ACTV: ServiceCards.ActivityCard,
  CAR: ServiceCards.CarCard,
  FLTS: ServiceCards.FlightCard,
  REST: ServiceCards.RestaurantCard,
  TRN: ServiceCards.TrainCard,
  TRAN: ServiceCards.TransferCard
};

/**
 * Functional React component for rendering a custom library item preview.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {boolean} [props.item] - The library item.
 * @param {boolean} [props.isOpenModal] - Open modal state.
 * @param {function} [props.onCancel] - Function to handle closing modal.
 * @param {...any} props.rest - Additional props to be spread on the element.
 *
 * @returns {JSX.Element} React element representing the library item preview.
 */

const LibraryPreview = ({ item, isOpenModal, onCancel, ...rest }) => {
  const { t } = useTranslation(['common', 'library']);
  const {
    contentid = 0,
    contentimage = [],
    code = 'day',
    note,
    service,
    tenantid,
    servicetypecode
  } = item || {};

  const title = item?.title || t('noTitle');
  const description = item?.description?.trim() || t('noDescription');
  const imageUrl = contentimage?.[0]?.imageurl || null;
  const iconref = service?.servicetype?.iconref;
  const CardComponent = ServiceCardComponent[servicetypecode] ?? null;

  const iconExists = (iconName) => {
    return typeof P[iconName] === 'object';
  };

  const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;

  const getAttachementUrl = (fileName = '') => {
    return getBucketFileUrl({
      fileName,
      pathReference: pathReference.LIBRARY,
      tenantId: tenantid
    });
  };

  const getImageUrl = (fileName = '') => {
    return getBucketImageUrl({
      fileName,
      pathReference: pathReference.LIBRARY,
      tenantId: tenantid,
      sizePrefix: imageSize.thumbnail.prefix
    });
  };

  const getPreviewFormFields = () => {
    return service?.servicefields?.reduce((acc, field) => {
      let values = field.options.map((opt) => opt?.value);

      // If only one option, store it as a single value instead of an array
      values = values.length > 1 ? values : values[0] || '';

      // Ensure 'MEALSINCLUDED' is always an array
      if (field.key === 'MEALSINCLUDED' && !Array.isArray(values)) {
        values = values ? [values] : [];
      }

      // Transform 'UPLOADIMAGES' field values to image URLs
      if (field.key === 'UPLOADIMAGES') {
        values = Array.isArray(values)
          ? values.map((value) => getImageUrl(value)).filter(Boolean)
          : values
            ? [getImageUrl(values)]
            : [];
      }

      // Transform 'UPLOADATTACHMENTS' field values to URLs
      if (field.key === 'UPLOADATTACHMENTS') {
        values = Array.isArray(values)
          ? values
              .map((fileName) => getAttachementUrl(fileName))
              .filter(Boolean)
          : values
            ? [getAttachementUrl(values)]
            : [];
      }

      acc[field.key] = values;
      return acc;
    }, {});
  };

  const fields = getPreviewFormFields();

  return (
    <XGModal
      id={`library-preview-${contentid}`}
      allowClosing={true}
      data-testid={`library-preview-${contentid}`}
      actions={<></>}
      dismissable={true}
      open={isOpenModal}
      title={
        <div className='library-preview__title-container'>
          {code === 'day' ? (
            <Sun color='#344054' size={28} />
          ) : code === 'service' ? (
            ServiceTypeIcon && (
              <div
                className={`service-view-card__header-icon service-view-card__header-icon-${iconref}`}
              >
                <ServiceTypeIcon size={21.33} />
              </div>
            )
          ) : (
            <File color='#344054' size={28} />
          )}
          {title}
        </div>
      }
      maxWidth='lg'
      scroll='paper'
      onClose={onCancel}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      {...rest}
    >
      <Box className='library-preview__modal-container'>
        {code == 'service' && CardComponent ? (
          <CardComponent
            servicefields={fields}
            servicetype={service?.servicetype}
            tripdetaileddayserviceid={service?.tripdetaileddayserviceid}
            tenantid={tenantid}
            isLibraryPreview={true}
          />
        ) : (
          <>
            {imageUrl ? (
              <Box className='library-preview__image-container '>
                <img
                  className='library-preview__image'
                  src={imageUrl}
                  alt={title}
                  draggable={false}
                />
              </Box>
            ) : (
              <> </>
            )}
            <Box className='library-preview__editor-container'>
              {item?.description?.trim() !== '' && (
                <p className='MuiTypography-root MuiTypography-body2 _create-trip__date-label'>
                  <span className='w-50'>{t('trip:description')} </span>
                </p>
              )}
              <Editor
                editable={false}
                isPreviewing={true}
                content={description}
                id={`library-preview-editor-description-${contentid}`}
                setDescription={() => {}}
                setMarkdown={() => {}}
                setIsPreviewing={() => {}}
                showActions={false}
                onCancelAction={() => {}}
                onSaveAction={() => {}}
                generatePrompt={<></>}
              />
            </Box>
            {code === 'day' && note && (
              <Box className='library-preview__editor-container'>
                {note && note !== '' && (
                  <p className='MuiTypography-root MuiTypography-body2 _create-trip__date-label'>
                    <span className='w-50'>{t('trip:internalNotes')} </span>
                  </p>
                )}
                <Editor
                  editable={false}
                  isPreviewing={true}
                  content={note}
                  id={`library-preview-editor-note-${contentid}`}
                  setDescription={() => {}}
                  setMarkdown={() => {}}
                  setIsPreviewing={() => {}}
                  showActions={false}
                  onCancelAction={() => {}}
                  onSaveAction={() => {}}
                  generatePrompt={<></>}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </XGModal>
  );
};

LibraryPreview.propTypes = {
  item: PropTypes.object,
  isOpenModal: PropTypes.bool,
  onCancel: PropTypes.func
};

export default LibraryPreview;
